import { useMemo, useState } from 'react'

import { RowClassParams, RowDoubleClickedEvent } from 'ag-grid-enterprise'
import { isAfter, isWeekend } from 'date-fns'
import { ArrowLeft, LayersPlus, RefreshCcwDot, RotateCcw, Sidebar, SidebarClose, X } from 'lucide-react'

import { capitalise, cn, toISO8601 } from '@fortem-monorepo/js-utilities'
import { CustomAgGrid, createColDefs } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar, Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import {
  Badge,
  Button,
  DatePickerWithControls,
  Label,
  LoadingButton,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  SearchSelect,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@fortem-monorepo/react-ui'
import { PnlAttributions } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { Page } from '@/components/Page'
import { OPERATIONS } from '@/constants/links'
import { PERMISSIONS } from '@/constants/permissions'
import { AttributionsActionedBy } from '@/pages/operations/pnl-attributions/components/AttributionsActionedBy'
import { AttibutionsSecondTab } from '@/pages/operations/pnl-attributions/components/AttributionsSecondTab'
import { HistoryItem } from '@/pages/operations/pnl-attributions/components/HistoryItem'
import { PublishAttributionsButton } from '@/pages/operations/pnl-attributions/components/PublishAttributionsButton'
import { TradeRow } from '@/pages/operations/pnl-attributions/components/TradeRow'
import { PnlContextProvider, usePnlContext } from '@/pages/operations/pnl-attributions/context'
import {
  PnlAttributionsActionsProvider,
  usePnlAttributionsActions,
} from '@/pages/operations/pnl-attributions/pnl-attribution-actions'
import { useFunds } from '@/react-query/queries/funds/funds'
import { formatNumber } from '@/utilities/format'

export const PnLValidationPage: React.FC = () => {
  return (
    <Page metadata={{ title: 'P&L Validation' }} permissions={OPERATIONS.PNL_VALIDATION.permissions}>
      <PnlContextProvider>
        <PnlAttributionsActionsProvider>
          <PageContent />
        </PnlAttributionsActionsProvider>
      </PnlContextProvider>
    </Page>
  )
}

export const PageContent: React.FC = () => {
  const {
    params,
    setFundName,
    setValueDate,
    attributionsGroupedQuery,
    attributionsQuery,
    inspectingRow,
    isInspectingTrades,
    isInspectingSummary,
    setInspectingRow,
    setInspectingTrades,
    stateQuery,
    historyQuery,
    selectedBatchId,
    setSelectedBatchId,
    selectedBatchState,
  } = usePnlContext()

  const { data: funds, query: fundsQuery } = useFunds()

  const attributionsState = stateQuery.data

  const paramsSelected = !!params.fund_name && !!params.value_date
  const stateLoaded = !stateQuery.isFetching && stateQuery.data !== undefined

  const isUnstaged = stateLoaded && attributionsState === null
  const isStaged = !!selectedBatchState && selectedBatchState?.state === 'STAGED'
  const isPublished = !!selectedBatchState && selectedBatchState?.state === 'PUBLISHED'

  const { openRefreshAttributions, openStartValidating } = usePnlAttributionsActions()

  const coldefs = useMemo(() => aggregationColDefs(), [])

  const [historyOpen, setHistoryOpen] = useState(true)

  const numberOfPendingItems = (historyQuery.data ?? []).filter((item) => item.status === 'PENDING').length

  const [historyTab, setHistoryTab] = useState<PnlAttributions.AttributionRequestStatus | 'ALL'>('ALL')

  const visibleHistory = useMemo(() => {
    return (historyQuery.data ?? []).filter(
      (i) => i.status !== 'STALE' && (historyTab === 'ALL' ? true : historyTab === i.status),
    )
  }, [historyQuery.data, historyTab])

  return (
    <>
      <div className="flex-1 flex flex-col">
        <HeaderConfigBar className="flex items-end gap-4 p-3 border-b">
          <div className="space-y-2">
            <Label className={'text-muted-foreground'}>Fund</Label>
            <SearchSelect
              disabled={stateQuery.isFetching}
              size="sm"
              loading={fundsQuery.isFetching}
              placeholder={'Select a fund...'}
              options={funds.map((fund) => ({ label: fund.fund_name, value: fund.fund_name }))}
              value={params.fund_name}
              setValue={(val) => setFundName(val)}
            />
          </div>
          <div className="space-y-2">
            <Label className={'text-muted-foreground'}>Date</Label>
            <DatePickerWithControls
              buttonDisabled={stateQuery.isFetching}
              size={'sm'}
              onAfterChange={() => {
                setInspectingRow(null)
                setInspectingTrades(null)
              }}
              selected={params.value_date ? new Date(params.value_date) : undefined}
              onSelect={(date) => (date ? setValueDate(toISO8601(date)) : undefined)}
              todayButton={{ enabled: true }}
              disabled={(date) => isWeekend(date) || isAfter(date, new Date())}
            />
          </div>
          <LoadingButton
            size="sm"
            variant="outline"
            disabled={stateQuery.isFetching}
            icon={<RotateCcw />}
            onClick={() => stateQuery.refetch()}
          >
            Refresh state
          </LoadingButton>
        </HeaderConfigBar>
        {stateLoaded && !isUnstaged && (
          <HeaderConfigBar>
            <div className="flex items-center gap-2 w-full">
              {stateLoaded && !isUnstaged && (
                <Select value={selectedBatchId?.toString()} onValueChange={(val) => setSelectedBatchId(Number(val))}>
                  <SelectTrigger className={'min-w-45'} size="sm" disabled={(attributionsState?.length ?? 0) <= 1}>
                    <SelectValue placeholder="Select batch" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {stateQuery.data?.map((batch) => {
                        return (
                          <SelectItem value={batch.batch_id.toString()}>
                            {batch.state}{' '}
                            <span className={'text-xs text-muted-foreground'}>
                              {new Intl.DateTimeFormat('en-GB', {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              }).format(new Date(batch.actioned_datetime))}
                            </span>
                          </SelectItem>
                        )
                      })}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className={'relative ml-auto'}
                    disabled={!historyQuery.data?.length}
                    onClick={() => setHistoryOpen(!historyOpen)}
                  >
                    {!historyOpen && (
                      <>
                        Show requests <Sidebar />
                      </>
                    )}
                    {historyOpen && (
                      <>
                        Hide requests <SidebarClose />
                      </>
                    )}
                    {!historyQuery.isFetching && numberOfPendingItems > 0 && (
                      <div
                        className={
                          'absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 aspect-square size-4 p-1 rounded-full flex items-center justify-center text-white bg-red-500 text-xs'
                        }
                      >
                        {numberOfPendingItems}
                      </div>
                    )}
                  </Button>
                </TooltipTrigger>
                {numberOfPendingItems > 0 && (
                  <TooltipContent>
                    <span className={'font-medium'}>{numberOfPendingItems}</span> pending items to review.
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </HeaderConfigBar>
        )}
        <div className="flex flex-1">
          <ResizablePanelGroup
            direction={'horizontal'}
            className={cn('flex flex-col h-full flex-1 border-3 border-gray-50 transition-colors', {
              'border-amber-400': isStaged && !stateQuery.isFetching,
              'border-emerald-400': isPublished && !stateQuery.isFetching,
            })}
          >
            <ResizablePanel className="flex" id="attribution-state" order={1}>
              <div className="flex flex-1 gap-8">
                <div className="flex flex-col flex-1 bg-gray-50 rounded-md shadow-xs">
                  {!paramsSelected && !stateQuery.isFetching && (
                    <span className="text-muted-foreground text-sm p-8">Select a fund and date to get started.</span>
                  )}
                  {stateQuery.isFetching && !selectedBatchState && (
                    <div className={'inline-flex gap-2 items-center p-8 text-muted-foreground text-sm'}>
                      <span>Loading attributions...</span> <Spinner size={16} />
                    </div>
                  )}
                  {!stateQuery.isFetching && isUnstaged && (
                    <div className="space-y-2 p-8">
                      <p className="text-muted-foreground text-sm">
                        No attributions are currently staged or published.
                      </p>
                      <div className="inline-flex items-center gap-2">
                        <span className="text-muted-foreground text-sm italic">Click</span>
                        <Button size="sm" variant="outline" onClick={() => openStartValidating(params)}>
                          Stage new attributions <LayersPlus />
                        </Button>
                        <span className="text-muted-foreground text-sm italic">
                          to begin validating attributions for {params.value_date}.
                        </span>
                      </div>
                    </div>
                  )}
                  {stateQuery.data && !isUnstaged && (
                    <Widget exposed className={cn('flex flex-col h-full')}>
                      <WidgetHeaderWithQuery
                        query={stateQuery}
                        title={`Current attributions for ${params.fund_name} on ${params.value_date}`}
                        afterTitle={
                          <div className="flex items-center gap-2 px-4">
                            {!stateQuery.isFetching && (
                              <>
                                {isStaged && <Badge theme="warning">STAGED BY</Badge>}
                                {isPublished && <Badge theme="success">PUBLISHED BY</Badge>}
                                {selectedBatchState?.actioned_by_name && (
                                  <AttributionsActionedBy name={selectedBatchState.actioned_by_name} />
                                )}
                                <CanAccess permissions={[PERMISSIONS.WRITE_PNL_VALIDATION]}>
                                  <Button size="sm" variant="outline" onClick={() => openRefreshAttributions(params)}>
                                    Start new attributions <RefreshCcwDot />
                                  </Button>
                                  {selectedBatchState?.state === 'STAGED' && <PublishAttributionsButton />}
                                </CanAccess>
                              </>
                            )}
                          </div>
                        }
                      />
                      <WidgetContent loading={stateQuery.isFetching} className={'flex flex-col flex-1'}>
                        <CustomAgGrid
                          domLayout={'normal'}
                          rowClassRules={{
                            'bg-slate-200! hover:bg-slate-200!': (
                              params: RowClassParams<PnlAttributions.AggregatedAttributionRow>,
                            ) => {
                              return (
                                inspectingRow?.reference_unique_identifier === params.data?.reference_unique_identifier
                              )
                            },
                          }}
                          onRowDoubleClicked={(
                            event: RowDoubleClickedEvent<PnlAttributions.AggregatedAttributionRow>,
                          ) => {
                            if (event.data) {
                              setInspectingTrades(null)
                              setInspectingRow(event.data)
                            }
                          }}
                          rowData={selectedBatchState?.data.sort((a, b) =>
                            a.reference_instrument_type.localeCompare(b.reference_instrument_type),
                          )}
                          query={stateQuery}
                          columnDefs={coldefs}
                        />
                      </WidgetContent>
                    </Widget>
                  )}
                </div>
              </div>
            </ResizablePanel>
            {(isInspectingSummary || isInspectingTrades) && (
              <>
                <ResizableHandle />
                <ResizablePanel id="attribution-details" order={2} className="flex">
                  {isInspectingSummary && !isInspectingTrades && (
                    <Widget exposed className="h-full">
                      <WidgetHeaderWithQuery
                        title={`Inspecting attributions for ${inspectingRow?.reference_unique_identifier}`}
                        query={attributionsGroupedQuery}
                        beforeTitle={
                          <Button size="sm" variant="outline" onClick={() => setInspectingRow(null)}>
                            Close <X />
                          </Button>
                        }
                      />
                      <WidgetContent loading={attributionsGroupedQuery.isFetching} className="flex flex-col flex-1">
                        {attributionsGroupedQuery.data && <AttibutionsSecondTab data={attributionsGroupedQuery.data} />}
                      </WidgetContent>
                    </Widget>
                  )}
                  {isInspectingTrades && (
                    <Widget exposed className="h-full">
                      <WidgetHeaderWithQuery
                        query={attributionsQuery}
                        beforeTitle={
                          <Button size="sm" variant="outline" onClick={() => setInspectingTrades(null)}>
                            Back <ArrowLeft />
                          </Button>
                        }
                        title={`Inspecting trades for ${inspectingRow?.reference_unique_identifier}`}
                      />
                      <WidgetContent loading={attributionsQuery.isFetching} className="flex flex-col flex-1">
                        <div className="flex flex-col flex-1">
                          <div className="relative h-full">
                            <div className="absolute top-0 left-0 h-full w-full p-4 overflow-y-auto space-y-4">
                              {(attributionsQuery.data ?? []).map((trade) => {
                                return <TradeRow trade={trade} key={trade.trade_id} />
                              })}
                            </div>
                          </div>
                        </div>
                      </WidgetContent>
                    </Widget>
                  )}
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>
          {stateLoaded && Array.isArray(historyQuery.data) && historyQuery.data.length > 0 && (
            <aside
              className={cn(
                'relative overflow-hidden w-full transition-[max-width] duration-700 ease-in-out',
                historyOpen ? 'max-w-170' : 'max-w-0',
              )}
            >
              <div
                className={cn(
                  'absolute top-0 left-0 h-full w-170 border-2 transition-transform duration-700 ease-in-out',
                )}
              >
                <Widget exposed className="h-full">
                  <WidgetHeaderWithQuery
                    className={'shrink-0'}
                    query={historyQuery}
                    title={`Requests for ${params.fund_name} on ${params.value_date}`}
                    beforeTitle={
                      <Button size="sm" variant="outline" onClick={() => setHistoryOpen(false)}>
                        <SidebarClose />
                      </Button>
                    }
                  />
                  <WidgetContent loading={historyQuery.isFetching} className={'flex flex-col overflow-hidden'}>
                    <HeaderConfigBar
                      className={cn('py-1 shrink-0', {
                        '-z-10': historyQuery.isFetching,
                      })}
                    >
                      <Tabs
                        value={historyTab}
                        onValueChange={(tab) => setHistoryTab(tab as typeof historyTab)}
                        className={' gap-0!'}
                      >
                        <TabsList>
                          <TabsTrigger value="ALL">All</TabsTrigger>
                          {['PENDING', 'APPLIED', 'REJECTED'].map((status) => {
                            const numItems = (historyQuery.data ?? []).filter((i) => i.status === status).length

                            if (!numItems) return null

                            return (
                              <TabsTrigger value={status}>
                                {capitalise(status)}{' '}
                                <Badge
                                  className={cn({
                                    'bg-amber-600': status === 'PENDING',
                                    'bg-emerald-600': status === 'APPLIED',
                                    'bg-red-600': status === 'REJECTED',
                                  })}
                                >
                                  {numItems}
                                </Badge>
                              </TabsTrigger>
                            )
                          })}
                        </TabsList>
                      </Tabs>
                    </HeaderConfigBar>
                    <div className={'flex-1 min-h-0 p-8 space-y-8 overflow-y-auto'}>
                      {visibleHistory.map((historyItem) => {
                        return <HistoryItem row={historyItem} key={historyItem.component_request_id} />
                      })}
                    </div>
                  </WidgetContent>
                </Widget>
              </div>
            </aside>
          )}
        </div>
      </div>
    </>
  )
}

const pnlFormatter = ({ value }: { value: number }) => {
  return formatNumber(value, { decimalPlaces: 0, roundingMode: 'round' })
}

const pnlCellClassRules = {
  'text-emerald-700': (params: any) => (params.value ?? 0) > 0,
  'text-red-700': (params: any) => (params.value ?? 0) < 0,
}

const aggregationColDefs = () => {
  return createColDefs<PnlAttributions.AggregatedAttributionRow>([
    {
      field: 'reference_unique_identifier',
      headerName: 'Instrument',
      cellClass: 'text-center',
      headerClass: 'header-center',
      minWidth: 350,
      pinned: 'left',
    },
    {
      field: 'reference_instrument_type',
      headerName: 'Instrument Type',
      valueFormatter: (params) => {
        return (params.value ?? '').replaceAll('_', ' ')
      },
    },
    {
      headerName: 'P&L',
      children: [
        {
          field: 'net_pnl',
          headerName: 'Net',
          valueFormatter: pnlFormatter,
          cellClass: 'text-center',
          headerClass: 'header-center',
          cellClassRules: pnlCellClassRules,
          width: 120,
        },
        {
          field: 'market_pnl',
          headerName: 'Market',
          valueFormatter: pnlFormatter,
          cellClass: 'text-center',
          headerClass: 'header-center',
          cellClassRules: pnlCellClassRules,
          columnGroupShow: 'open',
          width: 120,
        },
        {
          field: 'gross_pnl',
          headerName: 'Gross',
          valueFormatter: pnlFormatter,
          cellClass: 'text-center',
          headerClass: 'header-center',
          cellClassRules: pnlCellClassRules,
          columnGroupShow: 'open',
          width: 120,
        },
        {
          field: 'reference_pnl',
          headerName: 'Reference',
          cellClass: 'text-center',
          valueFormatter: pnlFormatter,
          headerClass: 'header-center',
          cellClassRules: pnlCellClassRules,
          width: 130,
        },
      ],
    },
    {
      headerName: 'Trades',
      children: [
        {
          field: 'count_existing',
          headerName: 'Existing',
          cellClass: 'text-center',
          headerClass: 'header-center text-blue-600',
          cellClassRules: {
            'text-blue-600 font-medium': (params) => params.value > 0,
          },
          valueFormatter: (params) => {
            if (params.value > 0) {
              return params.value
            }
            return ''
          },
          width: 100,
        },
        {
          field: 'count_new',
          headerName: 'New',
          cellClass: 'text-center',
          headerClass: 'header-center text-purple-600',
          cellClassRules: {
            'text-purple-600 font-medium': (params) => params.value > 0,
          },
          valueFormatter: (params) => {
            if (params.value > 0) {
              return params.value
            }
            return ''
          },
          width: 80,
        },
        {
          field: 'count_maturing',
          headerName: 'Maturing',
          cellClass: 'text-center ',
          headerClass: 'header-center text-amber-600',
          cellClassRules: {
            'text-amber-600 font-medium': (params) => params.value > 0,
          },
          valueFormatter: (params) => {
            if (params.value > 0) {
              return params.value
            }
            return ''
          },
          width: 100,
        },
        {
          field: 'count_reference',
          headerName: 'Reference',
          cellClass: 'text-center',
          headerClass: 'header-center text-cyan-600',
          cellClassRules: {
            'text-cyan-600 font-medium': (params) => params.value > 0,
          },
          valueFormatter: (params) => {
            if (params.value > 0) {
              return params.value
            }
            return ''
          },
          width: 110,
        },
      ],
    },
  ])
}
